export const ELAPSED_TICK_MS = 100;
export const DECISECONDS_PER_SECOND = 10;
export const SECONDS_PER_MINUTE = 60;
export const WAIT_WORD_CYCLE_MS = 2000;

export const SCAN_AI_WAIT_WORDS = [
  "Matching",
  "Reviewing",
  "Imaging",
  "Mapping",
  "Scanning",
  "Checking",
  "Reading",
  "Narrowing",
  "Localising",
  "Comparing",
  "Weighing",
  "Sifting",
  "Tracing",
  "Charting",
  "Aligning",
  "Triaging",
  "Gathering",
  "Collating",
  "Scouting",
  "Pinpointing",
  "Calibrating",
  "Sequencing",
  "Sorting",
  "Parsing",
  "Orienting",
];

let thinkingStartedAtMs = null;
let waitWordDeck = [];
let waitWordIndex = 0;

export function beginThinkingClock(nowMs = Date.now()) {
  if (thinkingStartedAtMs == null) {
    thinkingStartedAtMs = nowMs;
  }
  return thinkingStartedAtMs;
}

export function clearThinkingClock() {
  if (thinkingStartedAtMs != null) {
    stepWaitWord();
  }
  thinkingStartedAtMs = null;
}

export function thinkingElapsedDeciseconds(nowMs = Date.now()) {
  const startedAtMs = thinkingStartedAtMs ?? nowMs;
  return Math.max(0, Math.floor((nowMs - startedAtMs) / ELAPSED_TICK_MS));
}

export function createShuffledDeck(words, random = Math.random) {
  const deck = [...words];
  for (let i = deck.length - 1; i > 0; i -= 1) {
    const swapIndex = Math.floor(random() * (i + 1));
    [deck[i], deck[swapIndex]] = [deck[swapIndex], deck[i]];
  }
  return deck;
}

function currentDeckWord() {
  if (waitWordDeck.length === 0) {
    waitWordDeck = createShuffledDeck(SCAN_AI_WAIT_WORDS);
    waitWordIndex = 0;
  }
  return waitWordDeck[waitWordIndex] ?? SCAN_AI_WAIT_WORDS[0];
}

export function peekWaitWord() {
  return currentDeckWord();
}

export function stepWaitWord() {
  currentDeckWord();
  waitWordIndex += 1;
  if (waitWordIndex >= waitWordDeck.length) {
    waitWordDeck = [];
    waitWordIndex = 0;
  }
  return currentDeckWord();
}

export function seedWaitWords(deck, index = 0) {
  waitWordDeck = [...deck];
  const lastIndex = Math.max(deck.length - 1, 0);
  waitWordIndex = Math.min(Math.max(index, 0), lastIndex);
}

export function resetWaitWords() {
  waitWordDeck = [];
  waitWordIndex = 0;
}

function prefersReducedMotion() {
  return Boolean(
    typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches
  );
}

function stopTimer(id, clear) {
  if (id != null) {
    clear(id);
  }
  return null;
}

export function formatElapsed(deciseconds) {
  const totalSeconds = Number(deciseconds) / DECISECONDS_PER_SECOND;
  const elapsedSeconds = Number.isFinite(totalSeconds)
    ? Math.max(0, totalSeconds)
    : 0;

  if (elapsedSeconds < SECONDS_PER_MINUTE) {
    return `${elapsedSeconds.toFixed(1)}s`;
  }

  const minutes = Math.floor(elapsedSeconds / SECONDS_PER_MINUTE);
  const seconds = elapsedSeconds % SECONDS_PER_MINUTE;
  return `${minutes}m ${seconds.toFixed(1)}s`;
}

export function registerAiChatLoader(Alpine) {
  Alpine.data("aiChatLoader", () => ({
    deciseconds: 0,
    currentWord: "",
    leavingWord: "",
    revolving: false,
    _tick: null,
    _wordTick: null,

    init() {
      beginThinkingClock();
      this.currentWord = peekWaitWord();
      this.deciseconds = thinkingElapsedDeciseconds();
      this._tick = setInterval(() => {
        this.deciseconds = thinkingElapsedDeciseconds();
      }, ELAPSED_TICK_MS);

      if (!prefersReducedMotion()) {
        this._wordTick = setInterval(() => {
          this.revolveToNextWord();
        }, WAIT_WORD_CYCLE_MS);
      }
    },

    destroy() {
      this._tick = stopTimer(this._tick, clearInterval);
      this._wordTick = stopTimer(this._wordTick, clearInterval);
    },

    revolveToNextWord() {
      this.leavingWord = this.currentWord;
      this.currentWord = stepWaitWord();
      this.revolving = false;
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.revolving = true;
        });
      });
    },

    onWordRevolveEnd(event) {
      if (event.animationName !== "ai-chat-loader-word-in") {
        return;
      }

      this.revolving = false;
      this.leavingWord = "";
    },

    get elapsedLabel() {
      return formatElapsed(this.deciseconds);
    },
  }));
}
